import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useAuth } from '../context/AuthContext';
import { accepterInvitation, lireInvitation } from './api';
import themeAssure from './theme';

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

const NIVEAUX = {
  GESTION: 'consulter et compléter',
  LECTURE: 'consulter',
};

/**
 * La page qu'ouvre l'invité : accéder au dossier de quelqu'un d'autre.
 *
 * Il faut un compte, à l'adresse invitée : le lien a pu être transféré, et
 * c'est l'adresse vérifiée du compte qui fait foi.
 */
export default function PageInvitation() {
  const { jeton } = useParams();
  const naviguer = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    lireInvitation(jeton)
      .then(setInvitation)
      .catch((e) => setErreur(e?.response?.data?.detail
        || 'Impossible de contacter le serveur.'));
  }, [jeton, isAuthenticated]);

  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to={`/login?suite=${encodeURIComponent(`/invitation/${jeton}`)}`} replace />;
  }

  const accepter = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const reponse = await accepterInvitation(jeton);
      naviguer(reponse.chemin || '/');
    } catch (e) {
      setErreur(e?.response?.data?.detail || "L'invitation n'a pas pu être acceptée.");
      setOccupe(false);
    }
  };

  const utilisable = invitation && invitation.statut === 'en attente' && !invitation.refus;

  return (
    <ThemeProvider theme={themeAssure}>
      <ScopedCssBaseline>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
          <Container maxWidth="sm">
            <Typography variant="h1" sx={{ mb: 1 }}>Invitation sur un dossier</Typography>

            {erreur && <Alert severity="warning" sx={{ mb: 3 }}>{erreur}</Alert>}
            {!invitation && !erreur && <CircularProgress size={28} />}

            {invitation && (
              <Stack spacing={3}>
                <Typography variant="body1">
                  {invitation.invitant || "Quelqu'un"} vous invite à{' '}
                  {NIVEAUX[invitation.level] || 'consulter'} son dossier de sinistre.
                  Vous travaillerez sur le même dossier, pas sur une copie.
                </Typography>

                {invitation.statut !== 'en attente' && (
                  <Alert severity="info">Cette invitation est {invitation.statut}.</Alert>
                )}
                {invitation.statut === 'en attente' && invitation.refus && (
                  <Alert severity="warning">{invitation.refus}</Alert>
                )}

                {utilisable && (
                  <>
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography variant="body2" color="text.secondary">Le dossier</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {invitation.dossier}
                        {invitation.reference ? ` — ${invitation.reference}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Invitation valable jusqu'au {dateCourte(invitation.expire_le)}
                      </Typography>
                    </Paper>
                    <Button variant="contained" size="large" onClick={accepter}
                            disabled={occupe}>
                      {occupe ? 'Un instant…' : 'Accepter et ouvrir le dossier'}
                    </Button>
                  </>
                )}

                <Button component={Link} to="/" color="inherit">
                  Aller à mon espace
                </Button>
              </Stack>
            )}
          </Container>
        </Box>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
